import {useCallback, useEffect, useMemo, useState} from 'react';
import {useHistory, useLocation, useParams} from 'react-router-dom';
import {Box, Button, Container, Slide, Stack, SxProps, Typography} from '@mui/material';
import {ArrowForwardIos} from '@mui/icons-material';
import moment from 'moment';
import {useTranslation} from 'react-i18next';
import {translations} from '../../../../i18n/translations';
import {useDailyCareLog} from '../../../../contexts/daily-care-log/context';
import TaskTable from '../../../../components/daily-care-log/task-table/task-table';
import {EntityUser} from '../../../../backend/responses/entity-user';
import {CareTaskComplementary} from '../../../../backend/interfaces/care-task-complementary';
import {EntityShiftsRepository} from '../../../../repositories/entity-shifts';
import {ScreenPaths} from '../../../../enums';
import {useAuth} from '../../../../contexts/auth/context';
import {RolePermissionType} from '../../../../backend/enums/role-permission-type';
import {useEntity} from '../../../../contexts/entity/context';
import {AffiliatedUser} from '../../../../backend/responses/affiliated-user';
import {useShiftDetailsSearchParams} from '../shift-details/use-shift-search-params';
import {EntityUserCareTasksRepository} from '../../../../repositories/entity-user-care-tasks';
import {CareTask} from '../../../../backend/interfaces/care-task';

type LocationState = {
  tasks: CareTaskComplementary[];
};

const textSx: SxProps = {
  color: 'var(--dcm-gray)',
  textAlign: 'center',
  fontSize: '18px',
  fontWeight: 500,
  lineHeight: '24px',
  letterSpacing: '0.225px',
};

const spanTextSx: SxProps = {
  fontWeight: 400,
};

const bottomSelectStackSx: SxProps = {
  position: 'fixed',
  borderTop: '2px solid var(--dcm-blue-light)',
  backgroundColor: 'var(--dcm-white-base)',
  height: '86px',
  width: '100%',
  zIndex: 999,
  bottom: 0,
  left: 0,
  justifyContent: 'center',
  paddingY: '10px',
};

const bottomSelectStackContainerSx: SxProps = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  gap: 0.5,
};

const selectedLabelSx: SxProps = {
  color: 'var(--dcm-blue-base)',
  fontSize: '14px',
  fontStyle: 'normal',
  fontWeight: 500,
  lineHeight: '24px',
  letterSpacing: '0.175px',
};

type GroupedTasksDetailsCareLogScreenProps = {
  users?: AffiliatedUser[];
};

export default function GroupedTasksDetailsCareLogScreen({
  users,
}: GroupedTasksDetailsCareLogScreenProps) {
  const [shiftCareTasks, setShiftCareTasks] = useState<CareTaskComplementary[] | undefined>(
    undefined
  );
  const [selectedTasksIds, setSelectedTasksIds] = useState<string[]>([]);
  const {t} = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const dailyCareLogContext = useDailyCareLog();
  const {state: entityState} = useEntity();
  const {hasWritePermission} = useAuth();
  const canWriteDailyCareLog = hasWritePermission(RolePermissionType.DailyCareLog);
  const {
    entityId,
    entityShiftId,
    activityTypeId,
  }: {
    entityId: string;
    entityShiftId: string;
    activityTypeId: string;
  } = useParams();
  const {date, startDate, endDate, groupTime, groupName, careServiceUserId} =
    useShiftDetailsSearchParams();

  const fetchShiftCareTasks = useCallback(async () => {
    if (!groupTime) {
      history.goBack();
      return;
    }

    try {
      let caretasks: CareTaskComplementary[] | CareTask[];
      // --- Care Service HACK ---
      if (careServiceUserId) {
        caretasks = await EntityUserCareTasksRepository.instance
          .GetCareTasksWithComplementaryInformation(
            entityId,
            careServiceUserId,
            startDate.utc().toISOString(),
            endDate.utc().toISOString()
          )
          .then((response) => response.data);
      } else {
        caretasks = await EntityShiftsRepository.instance.getShiftCareTasks(
          entityId,
          entityShiftId,
          startDate.utc().toISOString(),
          endDate.utc().toISOString(),
          true,
          {
            headers: {
              'cache-control': 'no-cache',
            },
          }
        );
      }

      // ##############################################################
      // HACK: Backend should return care tasks from active users only
      // TODO: Remove this hack when backend is fixed
      // ##############################################################
      const filterCareTasks = (caretasks as CareTaskComplementary[])?.filter((task) =>
        users?.find((u) => u.user?.id === task.careTask.userId)
      );

      if (!filterCareTasks) return undefined;

      const groupedActivities = filterCareTasks.reduce(
        (result, item) => {
          // Skip Tasks if:
          if (
            // A. groupName is provided and does not match the care task name
            (groupName && item.careTask.nameConfigurationValue !== groupName) ||
            // B. groupName is not provided and care task has a name
            (!groupName && item.careTask.nameConfigurationValue)
            // ----
            // This means we want to show tasks with a custom name only if groupName is provided.
            // If groupName is not provided, we want to show tasks with no custom name.
            // ----
          ) {
            // Skip
            return result;
          }

          const date = moment(item.careTask.startsAt);
          const key = `${item.careTask.activityTypeId}_${date.get('hours')}_${date.get('minutes')}`;
          (result[key] = result[key] || []).push(item);
          return result;
        },
        {} as Record<string, CareTaskComplementary[]>
      );

      const time = moment(groupTime, 'HH:mm');

      setShiftCareTasks(
        groupedActivities[`${activityTypeId}_${time.get('hours')}_${time.get('minutes')}`]
      );
    } catch (error) {
      console.error(`🔴 Error: ${error}`);
    }
  }, [
    activityTypeId,
    entityId,
    entityShiftId,
    users,
    history,
    startDate,
    endDate,
    groupTime,
    groupName,
    careServiceUserId,
  ]);

  const seniorsMap: Map<string, EntityUser> = useMemo(() => {
    const map = new Map<string, EntityUser>();

    users?.forEach((senior) => map.set(senior.user!.id!, senior));

    return map;
  }, [users]);

  const redirectToGroupedTaskHandle = () => {
    if (!shiftCareTasks) return;

    const {pathname, search} = location;

    const handleInBulk = selectedTasksIds?.length > 1;

    if (handleInBulk) {
      let path = pathname + '/handle';

      const selectedTasks = shiftCareTasks.filter((task) =>
        selectedTasksIds.includes(task.careTask.id!)
      );

      const selectedRefs = selectedTasks.map((task) => task.careTask.ref);

      const searchParams = new URLSearchParams(search);
      searchParams.append('taskRef', selectedRefs.join(','));

      path += `?${searchParams.toString()}`;

      history.push(path, {tasks: selectedTasks});
    } else {
      const singularTaskId = selectedTasksIds[0];

      const selectedTask = shiftCareTasks.find((task) => singularTaskId === task.careTask.id);

      if (!selectedTask) return;

      history.push(
        `${ScreenPaths.DailyCareLog}/${entityId}${ScreenPaths.DailyCareLog_Tasks}/${selectedTask.careTask.ref}`,
        {task: selectedTask}
      );
    }
  };

  useEffect(() => {
    if (location.state) {
      setShiftCareTasks((location.state as LocationState).tasks);

      // Clear location.state so that it's not reused on getting back to this screen from the edit page
      window.history.replaceState(null, '');
    } else {
      fetchShiftCareTasks();
    }
  }, [fetchShiftCareTasks, location.state]);

  useEffect(() => {
    dailyCareLogContext.setNavigationHeader(true, t(translations.daily_care_log.generics.back));
    dailyCareLogContext.toggleBottomNavigation(true);

    return () => {
      dailyCareLogContext.setNavigationHeader(false);
      dailyCareLogContext.toggleBottomNavigation(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Stack gap={2} mt={0.5}>
      {shiftCareTasks && (
        <Stack flexDirection="row" gap={1}>
          <Typography sx={textSx} noWrap>
            <Typography component="span" sx={spanTextSx}>
              {moment(shiftCareTasks?.[0].careTask.startsAt).format('HH:mm')}
            </Typography>{' '}
            {shiftCareTasks?.[0].careTask.nameConfigurationValue ??
              shiftCareTasks?.[0].activityTypeName}
          </Typography>
        </Stack>
      )}

      <Box position="relative">
        <TaskTable
          tasks={shiftCareTasks}
          seniors={seniorsMap}
          entitySocialResponses={entityState.availableSocialResponses}
          select={
            canWriteDailyCareLog
              ? {
                  selectedTasksIds,
                  setSelectedTasksIds,
                }
              : undefined
          }
          displayType="user"
          entityShiftId={entityShiftId}
          date={date.format('YYYY-MM-DD')}
        />
        <Slide in={selectedTasksIds.length > 0} direction="up">
          <Stack sx={bottomSelectStackSx}>
            <Container sx={bottomSelectStackContainerSx}>
              <Box minHeight="24px">
                {selectedTasksIds.length > 0 && (
                  <Typography sx={selectedLabelSx}>
                    {selectedTasksIds.length > 1
                      ? t(
                          translations.daily_care_log.grouped_tasks_details.select_bottom_bar
                            .count_plural,
                          {count: selectedTasksIds.length}
                        )
                      : t(
                          translations.daily_care_log.grouped_tasks_details.select_bottom_bar.count,
                          {
                            count: 1,
                          }
                        )}
                  </Typography>
                )}
              </Box>

              <Button
                onClick={redirectToGroupedTaskHandle}
                variant="contained"
                disabled={selectedTasksIds.length === 0}
                endIcon={<ArrowForwardIos fontSize="small" />}
                sx={{
                  width: '252px',
                  textTransform: 'capitalize',
                  fontSize: '18px',
                  fontWeight: '500!important',
                  borderRadius: '5px',
                }}
              >
                {t(translations.daily_care_log.grouped_tasks_details.select_bottom_bar.next)}
              </Button>
            </Container>
          </Stack>
        </Slide>
      </Box>
    </Stack>
  );
}
